import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Button, Checkbox, FormControlLabel, Tooltip, Typography} from '@mui/material';
import DashboardShell from '../components/DashboardShell';
import {DashboardProvider, useDashboard} from '../context/DashboardContext';
import {
    GOLD,
    GREEN,
    READINESS,
    TRACE_REQS,
    VALIDATION_CRITERIA,
    VALIDATION_NOTICE_SHORT,
    VALIDATION_OUTCOMES,
    ring,
} from '../data';

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69', navyDeep: '#003D69', cyan: '#0096C8', cyanDeep: '#007DB4',
    white: '#FFFFFF', mist: 'var(--cpc-surface-subtle, #F5F8FB)', muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))', rust: '#c0553a',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const cardSx = {background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))'};

const ReviewProgressPage = () => {
    const history = useHistory();
    const {progress, confirms, toggleConfirm, approverConfirmed, setApproverConfirmed} = useDashboard();

    useEffect(() => {
        document.title = 'Review Progress and Submission';
    }, []);

    const ringColor = progress.pct >= 100 ? GREEN : GOLD;
    const ringBg = ring(progress.pct, ringColor);

    const progressRows = [
        {k: 'Overall review progress', v: `${progress.pct}%`},
        {k: 'Indicators reviewed', v: `${progress.indReviewed} of ${progress.TOTAL_IND}`, indent: true},
        {k: 'Indicators agreed', v: progress.indAgreed, indent: true},
        {k: 'Indicators with revisions proposed', v: progress.indProposed, indent: true},
        {k: 'Assessment items reviewed', v: `${progress.itemsReviewed} of ${progress.TOTAL_ITEMS}`, indent: true},
        {k: 'Assessment items agreed', v: progress.itemsAgreed, indent: true},
        {k: 'Assessment items with revisions proposed', v: progress.itemsProposed, indent: true},
        {k: 'Proposed revisions with complete rationale and evidence', v: `${progress.itemsComplete} of ${progress.itemsProposed}`, indent: true},
    ];

    const submitChecks = [
        {key: 'careful', label: 'All reviewed indicators were assessed carefully', checked: confirms.careful, toggle: () => toggleConfirm('careful')},
        {key: 'evidence', label: 'All proposed revisions include complete rationale and evidence', checked: confirms.evidence, toggle: () => toggleConfirm('evidence')},
        {key: 'unchanged', label: 'Unreviewed indicators should remain unchanged', checked: confirms.unchanged, toggle: () => toggleConfirm('unchanged')},
        {key: 'approver', label: 'The authorized approver has confirmed this submission', checked: approverConfirmed, toggle: () => setApproverConfirmed(!approverConfirmed)},
    ];

    const submitReqs = [
        {label: 'At least one indicator or assessment item has been reviewed', ok: progress.started},
        {label: 'Every proposed revision includes a complete rationale', ok: progress.evidenceOk},
        {label: 'Every proposed revision includes traceable supporting evidence', ok: progress.evidenceOk},
        {label: 'The authorized approver has confirmed the submission', ok: approverConfirmed},
    ];

    return (
        <DashboardShell active="progress">
            <Box
                sx={{
                    padding: '44px 56px 90px',
                    '@media (max-width: 1439px)': {paddingLeft: '40px', paddingRight: '28px'},
                    '@media (max-width: 720px)': {padding: '28px 20px 64px'},
                }}
            >
                <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.25}}>
                    Review Progress
                </Typography>
                <Typography component="h1" sx={{margin: '0 0 12px', fontSize: 28, fontWeight: 700, color: C.text}}>
                    Review Progress and Submission
                </Typography>
                <Typography sx={{fontSize: 19, lineHeight: 1.55, color: C.muted, margin: '0 0 32px'}}>
                    Track review completion across the national checklist, confirm submission readiness, and
                    submit the validated review to the CPC Index team. The full review workflow is described on
                    the{' '}
                    <Box
                        component="span"
                        onClick={() => history.push('/dq_pro/cpc-government-dashboard')}
                        sx={{fontWeight: 700, color: C.text, textDecoration: 'none', cursor: 'pointer'}}
                    >
                        <strong>About</strong>
                    </Box>{' '}
                    page.
                </Typography>

                <Box sx={{...cardSx, padding: '28px 30px', mb: 4.5, display: 'flex', gap: 5, flexWrap: 'wrap'}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, flex: 'none'}}>
                        <Tooltip title={`${progress.pct}% reviewed`} arrow>
                            <Box sx={{width: 132, height: 132, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: ringBg, cursor: 'default'}}>
                                <Box sx={{width: 104, height: 104, borderRadius: '50%', background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: C.text}}>
                                    {progress.pct}%
                                </Box>
                            </Box>
                        </Tooltip>
                        <Typography sx={{fontSize: 13, fontWeight: 700, color: C.muted}}>Overall review progress</Typography>
                    </Box>
                    <Box sx={{flex: 1, minWidth: 280}}>
                        {progressRows.map((r) => (
                            <Box
                                key={r.k}
                                sx={{display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.06))', fontSize: 14, ...(r.indent && {pl: 2.25})}}
                            >
                                <Box component="span" sx={{color: C.text}}>{r.k}</Box>
                                <Box component="span" sx={{fontWeight: 700}}>{r.v}</Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/*<Typography component="h2" sx={{fontSize: 22, fontWeight: 700, color: C.navy, margin: '0 0 18px'}}>Submission Readiness</Typography>*/}
                {/*<Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr 1fr', sm: 'repeat(4, 1fr)'}, gap: 1.75, mb: 3}}>*/}
                {/*    {READINESS.map((r) => {*/}
                {/*        const isCurrent = r.key === progress.readinessKey;*/}
                {/*        return (*/}
                {/*            <Box*/}
                {/*                key={r.key}*/}
                {/*                sx={{*/}
                {/*                    border: '1px solid',*/}
                {/*                    borderColor: isCurrent ? r.dot : 'rgba(0,0,0,.09)',*/}
                {/*                    background: isCurrent ? '#fbfbf8' : C.white,*/}
                {/*                    padding: '16px 16px',*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>*/}
                {/*                    <Box sx={{width: 10, height: 10, borderRadius: '50%', flex: 'none', background: r.dot}}/>*/}
                {/*                    <Typography sx={{fontSize: 14.5, fontWeight: 700, color: r.fg}}>{r.label}</Typography>*/}
                {/*                </Box>*/}
                {/*                <Typography sx={{fontSize: 12.5, color: C.muted, lineHeight: 1.45, margin: 0}}>{r.meaning}</Typography>*/}
                {/*                {isCurrent && (*/}
                {/*                    <Typography component="span" sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.cyanDeep, mt: 1, display: 'block'}}>*/}
                {/*                        Current status*/}
                {/*                    </Typography>*/}
                {/*                )}*/}
                {/*            </Box>*/}
                {/*        );*/}
                {/*    })}*/}
                {/*</Box>*/}

                <Box sx={{background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '20px 24px', mb: 2.5}}>
                    <Typography sx={{fontSize: 14.5, color: C.text, lineHeight: 1.5, mb: 1.75}}>
                        <strong>You do not need to review all 85 indicators before submitting.</strong> Before
                        submitting, confirm that:
                    </Typography>
                    {submitChecks.map((c) => (
                        <FormControlLabel
                            key={c.key}
                            sx={{display: 'flex', alignItems: 'center', gap: 0, mb: 1, ml: 0, fontSize: 14}}
                            control={<Checkbox checked={c.checked} onChange={c.toggle} size="small" sx={{color: C.cyanDeep, '&.Mui-checked': {color: C.cyanDeep}}}/>}
                            label={<Typography sx={{fontSize: 14}}>{c.label}</Typography>}
                        />
                    ))}
                </Box>

                <Button
                    disabled={!progress.ready}
                    sx={{
                        border: '2px solid',
                        borderColor: progress.ready ? C.rust : '#d8ccc6',
                        background: progress.ready ? C.rust : '#d8ccc6',
                        fontFamily: 'inherit',
                        fontSize: 15,
                        fontWeight: 700,
                        padding: '14px 26px',
                        color: C.white,
                        textTransform: 'none',
                        borderRadius: 0,
                        '&:hover': {background: progress.ready ? C.rust : '#d8ccc6'},
                        '&.Mui-disabled': {color: C.white, background: '#d8ccc6', borderColor: '#d8ccc6'},
                    }}
                >
                    Submit for Validation
                </Button>

                <Box sx={{mt: 2.5}}>
                    <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.text, mb: 1.25}}>The Submit for Validation button becomes available when:</Typography>
                    {submitReqs.map((q) => (
                        <Box key={q.label} sx={{display: 'flex', alignItems: 'center', gap: 1, fontSize: 13.5, mb: 0.75, color: q.ok ? '#3f7350' : '#8a8a82'}}>
                            <Box component="span">{q.ok ? '✓' : '○'}</Box>
                            {q.label}
                        </Box>
                    ))}
                </Box>

                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2.5, mt: 5.5}}>
                    <Box sx={{...cardSx, padding: '22px 24px'}}>
                        <Typography sx={{fontSize: 17, fontWeight: 700, color: C.text, mb: 1.25}}>Validation Notice</Typography>
                        <Typography sx={{fontSize: 13.5, lineHeight: 1.55, color: C.muted, mb: 1.75}}>{VALIDATION_NOTICE_SHORT}</Typography>
                        <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.text, mb: 1.25}}>The CPC Index review team will assess:</Typography>
                        <Box component="ul" sx={{margin: '0 0 16px', padding: 0, listStyle: 'none'}}>
                            {VALIDATION_CRITERIA.map((c) => (
                                <Box
                                    component="li"
                                    key={c}
                                    sx={{fontSize: 13, color: C.text, mb: 0.625, pl: 1.75, position: 'relative', '&::before': {content: '"·"', position: 'absolute', left: 0, color: C.rust, fontWeight: 700}}}
                                >
                                    {c}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {VALIDATION_OUTCOMES.map((o) => (
                                <Box key={o.label} component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, fontWeight: 700, padding: '6px 11px', background: C.mist}}>
                                    <Box sx={{width: 8, height: 8, borderRadius: '50%', background: o.color}}/>
                                    {o.label}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{background: C.surface, border: '1px solid rgba(192,85,58,.28)', padding: '22px 24px'}}>
                        <Typography sx={{fontSize: 17, fontWeight: 700, color: C.rust, mb: 1.25}}>Evidence Traceability Requirements</Typography>
                        <Typography sx={{fontSize: 13.5, color: C.muted, mb: 1.5}}>Evidence must be specific, authoritative, and independently traceable.</Typography>
                        {TRACE_REQS.map((t) => (
                            <Typography key={t.h} sx={{fontSize: 13, color: C.text, mb: 1, lineHeight: 1.5}}>
                                <strong>{t.h}</strong> {t.b}
                            </Typography>
                        ))}
                        <Typography sx={{fontSize: 13, color: C.muted, mt: 1.25, pt: 1.25, borderTop: '1px solid var(--cpc-border, rgba(0,60,105,.08))'}}>
                            A general link to a ministry homepage, an undated webpage, a search-results page, or an
                            unsupported statement should not be considered sufficient evidence.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </DashboardShell>
    );
};

const ReviewProgressPageWithProvider = () => (
    <DashboardProvider>
        <ReviewProgressPage/>
    </DashboardProvider>
);

export default ReviewProgressPageWithProvider;
